import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { useAuth } from "@/hooks/useAuth";

export interface DesignElement {
  id: string;
  type: "overlay" | "dom";
  // Overlay fields
  position_x: number;
  position_y: number;
  width: number;
  height: number;
  rotation: number;
  opacity: number;
  z_index: number;
  image_url?: string;
  page?: string;
  label?: string;
  is_active?: boolean;
  positionType?: "fixed" | "absolute" | "sticky";
  // DOM element fields
  domSelector?: string;
  domTagName?: string;
  text?: string;
  fontSize?: number;
  fontFamily?: string;
  textAlign?: string;
  textColor?: string;
  bgColor?: string;
  borderRadius?: number;
  // Layer system
  isLocked?: boolean;
  isHidden?: boolean;
}

export interface SelectedDomInfo {
  element: HTMLElement;
  selector: string;
  tagName: string;
  rect: DOMRect;
  text: string;
  computedStyle: {
    color: string;
    backgroundColor: string;
    fontSize: string;
    fontFamily: string;
    textAlign: string;
    borderRadius: string;
    opacity: string;
  };
}

export interface PendingDomOverride {
  selector: string;
  page: string;
  tagName: string;
  textContent: string;
  styles: Record<string, string>;
}

interface DesignModeContextType {
  isDesignMode: boolean;
  toggleDesignMode: () => void;
  // Overlay selection
  selectedElementId: string | null;
  selectElement: (id: string | null) => void;
  elements: DesignElement[];
  setElements: React.Dispatch<React.SetStateAction<DesignElement[]>>;
  updateElement: (id: string, updates: Partial<DesignElement>) => void;
  // DOM selection
  selectedDom: SelectedDomInfo | null;
  setSelectedDom: (info: SelectedDomInfo | null) => void;
  pendingDomOverrides: Record<string, PendingDomOverride>;
  upsertPendingDomOverride: (override: PendingDomOverride) => void;
  clearPendingDomOverrides: () => void;
  // State
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (v: boolean) => void;
  isPanelOpen: boolean;
  setPanelOpen: (v: boolean) => void;
  isAdmin: boolean;
}

const DesignModeContext = createContext<DesignModeContextType>({
  isDesignMode: false,
  toggleDesignMode: () => {},
  selectedElementId: null,
  selectElement: () => {},
  elements: [],
  setElements: () => {},
  updateElement: () => {},
  selectedDom: null,
  setSelectedDom: () => {},
  pendingDomOverrides: {},
  upsertPendingDomOverride: () => {},
  clearPendingDomOverrides: () => {},
  hasUnsavedChanges: false,
  setHasUnsavedChanges: () => {},
  isPanelOpen: false,
  setPanelOpen: () => {},
  isAdmin: false,
});

export const useDesignMode = () => useContext(DesignModeContext);

export const DesignModeProvider = ({ children }: { children: ReactNode }) => {
  const { role } = useAuth();
  const isAdmin = role === "admin";
  const [isDesignMode, setIsDesignMode] = useState(false);
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
  const [elements, setElements] = useState<DesignElement[]>([]);
  const [selectedDom, setSelectedDomState] = useState<SelectedDomInfo | null>(null);
  const [pendingDomOverrides, setPendingDomOverrides] = useState<Record<string, PendingDomOverride>>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isPanelOpen, setPanelOpen] = useState(false);

  const toggleDesignMode = useCallback(() => {
    setIsDesignMode((prev) => {
      if (prev) {
        setSelectedElementId(null);
        setSelectedDomState(null);
        setPanelOpen(false);
      }
      return !prev;
    });
  }, []);

  const selectElement = useCallback((id: string | null) => {
    setSelectedElementId(id);
    if (id) {
      setSelectedDomState(null);
    }
    setPanelOpen(!!id);
  }, []);

  const setSelectedDom = useCallback((info: SelectedDomInfo | null) => {
    setSelectedDomState(info);
    if (info) {
      setSelectedElementId(null);
    }
    setPanelOpen(!!info);
  }, []);

  const updateElement = useCallback((id: string, updates: Partial<DesignElement>) => {
    setElements((prev) =>
      prev.map((el) => (el.id === id ? { ...el, ...updates } : el))
    );
    setHasUnsavedChanges(true);
  }, []);

  const upsertPendingDomOverride = useCallback((override: PendingDomOverride) => {
    setPendingDomOverrides((prev) => ({ ...prev, [`${override.page}::${override.selector}`]: override }));
    setHasUnsavedChanges(true);
  }, []);

  const clearPendingDomOverrides = useCallback(() => {
    setPendingDomOverrides({});
  }, []);

  return (
    <DesignModeContext.Provider
      value={{
        isDesignMode: isAdmin && isDesignMode,
        toggleDesignMode,
        selectedElementId,
        selectElement,
        elements,
        setElements,
        updateElement,
        selectedDom,
        setSelectedDom,
        pendingDomOverrides,
        upsertPendingDomOverride,
        clearPendingDomOverrides,
        hasUnsavedChanges,
        setHasUnsavedChanges,
        isPanelOpen,
        setPanelOpen,
        isAdmin,
      }}
    >
      {children}
    </DesignModeContext.Provider>
  );
};
